import type { RegionLocation } from '~/types/regions'

const useSelectedRegion = () => useState<RegionLocation | null>('selected-region', () => null)
const useRegionsList = () => useState<RegionLocation[]>('regions-list', () => [])
const useRegionsLoading = () => useState<boolean>('regions-loading', () => false)

export const useRegions = () => {
  const { $services } = useNuxtApp()
  const regionsService = $services.regionsService
  const route = useRoute()

  const regionCookie = useCookie<string | null>('selected-region', {
    maxAge: 60 * 60 * 24 * 365,
    sameSite: 'lax',
    path: '/',
  })

  const selectedRegion = useSelectedRegion()
  const regions = useRegionsList()
  const loading = useRegionsLoading()

  const searchQuery = ref('')
  const searchResults = ref<RegionLocation[]>([])
  const searchLoading = ref(false)

  const setCookieRegion = (slug: string | null) => {
    if (import.meta.server) return
    regionCookie.value = slug || null
  }

  const selectRegionBySlug = (slug: string): RegionLocation | null => {
    const found = regions.value.find(r => r.slug === slug)
    if (found) {
      selectedRegion.value = found
      setCookieRegion(found.slug)
      return found
    }
    return null
  }

  const initRegion = () => {
    if (regions.value.length === 0) return

    const currentPath = route.path

    // 1. Всегда пробуем определить по URL (приоритет)
    const urlMatch = currentPath.match(/region-([a-z-]+)/)
    if (urlMatch && urlMatch[1]) {
      const slugFromUrl = urlMatch[1]
      if (selectedRegion.value?.slug !== slugFromUrl) {
        selectRegionBySlug(slugFromUrl)
      }
      return
    }

    // 2. Если регион уже выбран — не трогаем
    if (selectedRegion.value) return

    // 3. Пробуем из куки
    if (regionCookie.value && selectRegionBySlug(String(regionCookie.value))) {
      return
    }

    // 4. Дефолт — Москва
    const moscow = regions.value.find(r => r.name?.includes('Москва'))
    if (moscow) {
      selectedRegion.value = moscow
      setCookieRegion(moscow.slug)
      return
    }

    // 5. Первый из списка
    if (regions.value.length > 0 && regions.value[0]) {
      selectedRegion.value = regions.value[0]
      setCookieRegion(regions.value[0].slug)
    }
  }

  const loadRegions = async () => {
    if (loading.value) {
      while (loading.value) {
        await new Promise(resolve => setTimeout(resolve, 50))
      }
      initRegion()
      return
    }

    if (regions.value.length > 0) {
      initRegion()
      return
    }

    loading.value = true
    try {
      const data = await regionsService.getRegions()

      if (!data || !Array.isArray(data) || data.length === 0) {
        return
      }

      regions.value = data
      initRegion()
    } catch (error) {
      console.error('Failed to load regions:', error)
    } finally {
      loading.value = false
    }
  }

  const selectRegion = (region: RegionLocation | null) => {
    selectedRegion.value = region
    setCookieRegion(region?.slug || null)
  }

  const searchRegions = async (query: string) => {
    searchQuery.value = query

    if (!query.trim()) {
      searchResults.value = []
      return
    }

    searchLoading.value = true
    try {
      const data = await regionsService.getSearchRegions(query)
      searchResults.value = data
    } catch (error) {
      console.error('Failed to search regions:', error)
      searchResults.value = []
    } finally {
      searchLoading.value = false
    }
  }

  const clearSearch = () => {
    searchQuery.value = ''
    searchResults.value = []
  }

  const displayedRegions = computed(() => {
    if (!searchQuery.value) return regions.value
    return searchResults.value
  })

  return {
    regions: readonly(regions),
    selectedRegion: readonly(selectedRegion),
    loading: readonly(loading),
    searchQuery: readonly(searchQuery),
    searchResults: readonly(searchResults),
    searchLoading: readonly(searchLoading),
    displayedRegions: readonly(displayedRegions),
    loadRegions,
    selectRegion,
    searchRegions,
    clearSearch,
    getRegionBySlug: (slug: string) => regions.value.find(r => r.slug === slug),
    isRegionSelected: (slug: string) => selectedRegion.value?.slug === slug,
  }
}
